// ===========================================
// Email Sequences — list
// ===========================================
// Admin-authored drip series: a named list of emails sent over a set period,
// started per student from their details page.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { PermissionGate } from '../../components/common/PermissionGate';
import { useTenant } from '../../contexts/TenantContext';
import {
  useDeleteEmailSequence,
  useEmailSequenceStatus,
  useEmailSequences,
  useUpdateEmailSequence,
} from '../../hooks/useEmailSequence';
import { apiErrorMessage } from '../../utils/apiError';
import type { EmailSequence } from '../../types/emailSequence.types';

/** `9:00 am` — the hour a series sends, in the school's own timezone. */
export function formatSendHour(hour: number): string {
  const suffix = hour < 12 ? 'am' : 'pm';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:00 ${suffix}`;
}

/**
 * What the anchor means, in an admin's words rather than the enum's.
 *
 * `START` counts from the day you enrol a student; `DATE` counts back from a
 * date you pick per student (an exam, a concert).
 */
export function anchorLabel(anchor: EmailSequence['anchor']): string {
  return anchor === 'START' ? 'From enrolment' : 'From a date';
}

export default function EmailSequencesPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteTarget, setDeleteTarget] = useState<EmailSequence | null>(null);
  // ⚠️ D14 archives a used sequence rather than deleting it, so its history
  // survives — but nothing passed `includeArchived` until the whole-feature /qa
  // (2026-09-17), which made an archived series impossible to look at again.
  // The plumbing existed end to end (service, route, API client, hook key) and
  // had no caller: the same built-and-wired-to-nothing shape this feature hit
  // five times.
  const [showArchived, setShowArchived] = useState(false);

  const { data: sequences = [], isLoading, isError, refetch } = useEmailSequences(showArchived);
  // ⚠️ Deliberately NOT gated on an error: if this call fails the banner simply
  // does not render, which is the safe direction — a spurious "switched off"
  // warning on a working server would be worse than none.
  const { data: status } = useEmailSequenceStatus();
  const updateMutation = useUpdateEmailSequence();
  const deleteMutation = useDeleteEmailSequence();

  const handleToggleActive = async (sequence: EmailSequence) => {
    try {
      await updateMutation.mutateAsync({
        sequenceId: sequence.id,
        data: { isActive: !sequence.isActive },
      });
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err, 'Could not update this series'), { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const result = await deleteMutation.mutateAsync(deleteTarget.id);
      enqueueSnackbar(
        result.archived
          ? 'Series archived — its enrolment history is kept.'
          : 'Series deleted',
        { variant: 'success' }
      );
      setDeleteTarget(null);
    } catch (err) {
      // ⚠️ The 409 here is the useful one: "still running for N students".
      // Surfacing the server's own message beats a generic failure.
      enqueueSnackbar(apiErrorMessage(err, 'Could not delete this series'), { variant: 'error' });
      setDeleteTarget(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Email Sequences"
        subtitle="A series of emails sent to a family over time — onboarding a new student, or preparing for an exam"
        action={
          <PermissionGate resource="communications_emails" action="write">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/${schoolSlug}/admin/email-sequences/new`)}
            >
              New Sequence
            </Button>
          </PermissionGate>
        }
      />

      {/*
        ⚠️ PERSISTENT, not a toast. With the flag off an admin can author a
        series and start a student on it, and everything looks like it worked —
        the failure is SILENT and they would discover it days later when a
        family mentions receiving nothing. The wording says what still works, so
        it reads as a state of the server rather than an error they caused.
      */}
      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Switch
            size="small"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
        }
        label="Show archived"
      />

      {status?.enabled === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Sending is switched off on this server</AlertTitle>
          Series can be authored and started, and will send once sending is enabled.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Starts</TableCell>
              <TableCell align="right">Emails</TableCell>
              <TableCell>Sends at</TableCell>
              <TableCell align="right">Running</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(4)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              /*
                ⚠️ A THIRD STATE, enumerated rather than implied. React Query
                leaves `data` undefined while loading AND on error, so the
                `?? []` above would otherwise render "No sequences yet" for a
                failed request — telling an admin their series are gone when the
                server merely did not answer.
              */
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 4 }}>
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={() => refetch()}>
                        Retry
                      </Button>
                    }
                  >
                    Could not load email sequences.
                  </Alert>
                </TableCell>
              </TableRow>
            ) : sequences.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <RepeatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    No sequences yet. Create one to welcome new students or prepare a class for an exam.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sequences.map((sequence) => {
                // A series with live enrolments cannot be removed at all —
                // stopping those families is a separate, deliberate decision,
                // so the action is disabled with the reason rather than
                // offered and then refused by a 409.
                const hasLive = sequence.liveEnrolmentCount > 0;
                const deleteHint = hasLive
                  ? `Running for ${sequence.liveEnrolmentCount} student${
                      sequence.liveEnrolmentCount === 1 ? '' : 's'
                    } — stop those first`
                  : sequence.totalEnrolmentCount > 0
                    ? 'Archive this series (its history is kept)'
                    : 'Delete this series';

                return (
                  <TableRow
                    key={sequence.id}
                    hover
                    onClick={() =>
                      navigate(`/${schoolSlug}/admin/email-sequences/${sequence.id}`)
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {sequence.name}
                      </Typography>
                      {sequence.description && (
                        <Typography variant="caption" color="text.secondary">
                          {sequence.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" variant="outlined" label={anchorLabel(sequence.anchor)} />
                        {/* Otherwise an archived row reads as a live one. */}
                        {sequence.archivedAt && (
                          <Chip size="small" variant="outlined" color="default" label="Archived" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{sequence.steps.length}</TableCell>
                    <TableCell>{formatSendHour(sequence.sendHour)}</TableCell>
                    <TableCell align="right">{sequence.liveEnrolmentCount}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <PermissionGate resource="communications_emails" action="write">
                        <Switch
                          size="small"
                          checked={sequence.isActive}
                          onChange={() => handleToggleActive(sequence)}
                          inputProps={{
                            'aria-label': `${sequence.isActive ? 'Deactivate' : 'Activate'} ${sequence.name}`,
                          }}
                        />
                      </PermissionGate>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit this series">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/${schoolSlug}/admin/email-sequences/${sequence.id}`)
                            }
                            aria-label={`Edit ${sequence.name}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <PermissionGate resource="communications_emails" action="delete">
                          {/* A disabled IconButton fires no events, so the
                              Tooltip needs a real element to listen on — hence
                              the span. Without it the REASON for the disabled
                              state is invisible, which is the whole point. */}
                          <Tooltip title={deleteHint}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={hasLive}
                                onClick={() => setDeleteTarget(sequence)}
                                aria-label={`Delete ${sequence.name}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </PermissionGate>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={
          deleteTarget && deleteTarget.totalEnrolmentCount > 0
            ? 'Archive this series?'
            : 'Delete this series?'
        }
        message={
          deleteTarget && deleteTarget.totalEnrolmentCount > 0
            ? `"${deleteTarget.name}" has been used before, so it will be archived rather than deleted — the record of who received what is kept.`
            : `"${deleteTarget?.name}" will be permanently deleted.`
        }
        confirmLabel={
          deleteTarget && deleteTarget.totalEnrolmentCount > 0 ? 'Archive' : 'Delete'
        }
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
